import { memo } from 'react';
import { useEventCallback } from 'usehooks-ts';
import DynamicDialog, { type DynamicDialogProps } from '@discover-ui/dynamicDialog';
import Checkbox from '@discover-ui/checkbox';
import {
  CommandRoot,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@discover-ui/command';
import Button from '@discover-ui/button';
import CheckboxGroup from '@discover-ui/checkboxGroup';

type Option = { value: string; label: string; disabled?: boolean };

type SearchComboBoxProps = {
  category: string;
  options: Option[];
  setTagIds: (ids: number[]) => void;
  triggerProps?: DynamicDialogProps['triggerProps'];
  triggerLabel: React.ReactNode;
  currentTagIds?: number[];
  hasSearchInterface?: boolean;
};

const SearchOption = memo(function SearchOption({
  value,
  label,
  checked,
  onToggle,
}: {
  value: string;
  label: string;
  checked: boolean;
  onToggle: (value: string, checked: boolean) => void;
}) {
  return (
    <CommandItem
      value={label}
      onSelect={() => onToggle(value, !checked)}
      className="flex cursor-pointer flex-row items-center justify-between py-2 aria-selected:bg-transparent aria-selected:text-foreground"
    >
      <span className="flex-1">{label}</span>
      {/* Clicks, including keyboard activation, bubble to the tracked row. */}
      <Checkbox checked={checked} aria-label={label} />
    </CommandItem>
  );
});

export default function SearchComboBox({
  category,
  options,
  setTagIds,
  triggerProps,
  triggerLabel,
  currentTagIds = [],
  hasSearchInterface = true,
}: SearchComboBoxProps) {
  const tagStringValues = currentTagIds.map(String);
  const toggleOption = useEventCallback((value: string, checked: boolean) => {
    const id = parseInt(value, 10);
    setTagIds(checked ? [...currentTagIds, id] : currentTagIds.filter((tagId) => tagId !== id));
  });

  return (
    <DynamicDialog
      triggerLabel={triggerLabel}
      triggerProps={{
        ...triggerProps,
        kind: currentTagIds.length > 0 ? 'normal' : 'outline',
      }}
      dialogTitle={category}
      dialogDescription="Select one or more options"
      contentClassName="flex flex-col"
    >
      {({ setOpen }) => (
        <div className="flex min-h-0 flex-1 flex-col items-stretch p-1">
          {hasSearchInterface ? (
            <CommandRoot className="flex-1">
              <CommandInput className="my-2" placeholder={`Search ${category.toLowerCase()}...`} />
              <CommandList className="max-h-full sm:max-h-[300px]">
                <CommandEmpty>No results found.</CommandEmpty>
                <CommandGroup>
                  {options.map((option) => (
                    <SearchOption
                      key={option.value}
                      value={option.value}
                      label={option.label}
                      checked={tagStringValues.includes(option.value)}
                      onToggle={toggleOption}
                    />
                  ))}
                </CommandGroup>
              </CommandList>
            </CommandRoot>
          ) : (
            <div className="my-2 flex-1 overflow-y-auto">
              <CheckboxGroup
                options={options}
                values={tagStringValues}
                setValues={(newValues) => {
                  // Convert string values back to numbers for the parent component
                  setTagIds(newValues.map((value) => parseInt(value)));
                }}
              />
            </div>
          )}
          <div className="mt-auto flex flex-row justify-between gap-1">
            <Button
              width="wide"
              kind={currentTagIds.length > 0 ? 'outline' : 'gray'}
              disabled={currentTagIds.length === 0}
              onClick={() => {
                setTagIds([]);
              }}
            >
              Clear
              {currentTagIds.length > 0 ? (
                <span>
                  <strong>&nbsp;·&nbsp;</strong>
                  {currentTagIds.length}
                </span>
              ) : null}
            </Button>
            <Button width="wide" onClick={() => setOpen(false)}>
              Done
            </Button>
          </div>
        </div>
      )}
    </DynamicDialog>
  );
}
